import { ref, readonly, type Ref } from 'vue';

/**
 * A reactive ref that holds the current network status of the browser.
 * It's defined at the module level, making it a singleton (shared state).
 * @private
 */
const isOnline = ref(navigator.onLine);

/**
 * Updates the isOnline ref to true.
 * @private
 */
const handleOnline = () => {
  isOnline.value = true;
};

/**
 * Updates the isOnline ref to false.
 * @private
 */
const handleOffline = () => {
  isOnline.value = false;
};

// The event listeners are attached once when this module is first imported.
// They will remain active for the entire application lifecycle.
window.addEventListener('online', handleOnline);
window.addEventListener('offline', handleOffline);

/**
 * A function to manually remove the global 'online' and 'offline' event listeners.
 */
const stopListening = (): void => {
  window.removeEventListener('online', handleOnline);
  window.removeEventListener('offline', handleOffline);
};

/**
 * A Vue composable that provides a reactive boolean indicating the browser's
 * network status.
 *
 * @returns {{
 * isOnline: Readonly<Ref<boolean>>,
 * stopListening: () => void
 * }} An object containing a readonly ref of the network status and a cleanup function.
 */
export const useNetworkStatus = (): { isOnline: Readonly<Ref<boolean>>; stopListening: () => void } => {
  // Return a readonly version of the ref to prevent components
  // from accidentally changing its value.
  return {
    stopListening,
    isOnline: readonly(isOnline),
  };
};
